import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import MenuSetupService from "../../../services/KangarooServices/MenuSetupService";
import MenuColumn from "../component/MenuColumn";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import DeleteModal from "../../../Components/Common/DeleteModal";

const Menu = () => {
  const [typeData, setTypeData] = useState([]);
  const [menuData, setMenuData] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [isTypeOpen, setIsTypeOpen] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [editingType, setEditingType] = useState(null);

  const [pageSize] = useState(20);

  // Fetch menu types - we only need id and name
  const fetchType = async (page = 1) => {
    try {
      const response = await MenuSetupService.getType({ page, pageSize });
      // Extract only id and name from items, ignore the menus array
      const types = (response?.items || []).map(
        ({ id, name, createdAt, updatedAt }) => ({
          id,
          name,
          createdAt,
          updatedAt,
        })
      );
      setTypeData(types);
      console.log("Menu Types (cleaned):", types);
    } catch (error) {
      console.log(error);
      toast.error("Failed to fetch menu types");
    }
  };

  // Fetch menus - returns flat array
  const fetchMenu = async () => {
    try {
      const response = await MenuSetupService.get();
      console.log("Raw Menu API Response (flat array):", response);
      setMenuData(response || []);
    } catch (error) {
      console.log(error);
      toast.error("Failed to fetch menus");
    }
  };

  useEffect(() => {
    fetchType();
    fetchMenu();
  }, []);

  // Build hierarchical tree from flat array
  // CRITICAL: API returns flat array, we build the tree structure
  const buildMenuTree = (flatMenuArray, selectedTypeId = null) => {
    // 1. Filter parent menus
    let parentMenus = flatMenuArray.filter((menu) => menu.parentId === null);

    if (selectedTypeId) {
      parentMenus = parentMenus.filter((menu) =>
        menu.menuTypes?.some((type) => type.id === selectedTypeId)
      );
    }

    // 2. Sort children for each parent
    const treeStructure = parentMenus.map((parent) => ({
      ...parent,
      children: (parent.children || []).sort(
        (a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0)
      ),
    }));

    // 3. Sort parents
    return treeStructure.sort(
      (a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0)
    );
  };

  // Build the hierarchical menu structure
  const hierarchicalMenus = buildMenuTree(menuData, selectedTypeId);

  const toggleType = () => {
    setIsTypeOpen(!isTypeOpen);
    if (!isTypeOpen) {
      setEditingType(null);
      formik.resetForm();
    }
  };

  const toggleDelete = () => setIsOpenDelete(!isOpenDelete);

  const handleDeleteClick = (item) => {
    setSelectedDeleteItem(item);
    toggleDelete();
  };

  const handleDelete = async () => {
    if (selectedDeleteItem) {
      try {
        await MenuSetupService.deleteType(selectedDeleteItem.id);
        fetchType();
        fetchMenu();
        toast.success("Menu type deleted successfully");
        if (selectedTypeId === selectedDeleteItem.id) {
          setSelectedTypeId(null);
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Failed to delete menu type"
        );
      } finally {
        toggleDelete();
        setSelectedDeleteItem(null);
      }
    }
  };

  const handleEditType = (type) => {
    setEditingType(type);
    formik.setValues({
      id: type.id,
      name: type.name,
    });
    setIsTypeOpen(true);
  };

  // Count menus for each type
  const getMenuCountForType = (typeId) => {
    return menuData.filter(
      (m) => m.parentId === null && m.menuTypes?.some((t) => t.id === typeId)
    ).length;
  };

  const formik = useFormik({
    initialValues: {
      id: null,
      name: "",
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        if (values.id) {
          await MenuSetupService.updateType(values.id, { name: values.name });
          toast.success("Menu type updated successfully");
        } else {
          await MenuSetupService.createType({ name: values.name });
          toast.success("Menu type created successfully");
        }
        fetchType();
        fetchMenu();
        toggleType();
        formik.resetForm();
      } catch (error) {
        console.error(error);
        toast.error(error?.response?.data?.message || "Something went wrong!");
      }
    },
  });

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Menu" pageTitle="Menu Setup" />

        <Row>
          {/* LEFT COLUMN: MENU TYPES */}
          <Col lg={4}>
            <Card className="shadow-sm">
              <CardHeader className="bg-soft-primary">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="ri-folder-line me-2" />
                    Menu Types
                  </h5>
                  <button
                    className="btn btn-success btn-sm"
                    onClick={toggleType}
                  >
                    <i className="ri-add-line me-1" />
                    Add Type
                  </button>
                </div>
              </CardHeader>
              <CardBody>
                {typeData.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="ri-folder-open-line display-4 text-muted" />
                    <p className="text-muted mt-3">No menu types found</p>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={toggleType}
                    >
                      Create First Type
                    </button>
                  </div>
                ) : (
                  <div className="menu-types-list">
                    {typeData.map((type) => (
                      <div
                        key={type.id}
                        onClick={() => setSelectedTypeId(type.id)}
                        className={`p-3 rounded mb-2 border ${
                          selectedTypeId === type.id
                            ? "bg-primary text-white border-primary"
                            : "bg-light border-light"
                        }`}
                        style={{ cursor: "pointer", transition: "all 0.2s" }}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <strong className="d-block mb-1">
                              <i className="ri-folder-3-line me-2" />
                              {type.name}
                            </strong>
                            <small
                              className={
                                selectedTypeId === type.id
                                  ? "text-white-50"
                                  : "text-muted"
                              }
                            >
                              <i className="ri-menu-2-line me-1" />
                              {getMenuCountForType(type.id)} menu
                              {getMenuCountForType(type.id) !== 1 ? "s" : ""}
                            </small>
                          </div>

                          <div className="d-flex gap-1">
                            <button
                              className={`btn btn-sm ${
                                selectedTypeId === type.id
                                  ? "btn-light"
                                  : "btn-outline-primary"
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditType(type);
                              }}
                              title="Edit"
                            >
                              <i className="ri-edit-line" />
                            </button>
                            <button
                              className={`btn btn-sm ${
                                selectedTypeId === type.id
                                  ? "btn-light text-danger"
                                  : "btn-outline-danger"
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(type);
                              }}
                              title="Delete"
                            >
                              <i className="ri-delete-bin-5-line" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Show All Button */}
                {typeData.length > 0 && (
                  <button
                    className={`btn btn-sm w-100 mt-2 ${
                      selectedTypeId === null
                        ? "btn-primary"
                        : "btn-outline-secondary"
                    }`}
                    onClick={() => setSelectedTypeId(null)}
                  >
                    <i className="ri-apps-line me-1" />
                    Show All Types
                  </button>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* RIGHT COLUMN: MENUS BASED ON TYPE */}
          <Col lg={8}>
            <MenuColumn
              menus={hierarchicalMenus}
              onUpdate={async (id, payload) => {
                try {
                  await MenuSetupService.update(id, payload);
                  toast.success("Menu updated successfully");
                  fetchMenu();
                } catch (error) {
                  toast.error("Failed to update menu");
                  console.error("Update error:", error);
                }
              }}
              onReorder={async (payload) => {
                try {
                  for (const item of payload) {
                    await MenuSetupService.update({
                      title: item.title,
                      url: item.url,
                      id: item?.id || "",
                      isExternal: item.isExternal ?? false,
                      parentId: item.parentId || "", // API expects "" not null
                      menuTypeId: item.menuTypeId || "", // send even if unchanged
                      orderIndex: item.orderIndex ?? 0,
                      isActive: item.isActive ?? true,
                    });
                  }
                  toast.success("Menu order updated successfully");
                  fetchMenu();
                } catch (error) {
                  toast.error("Failed to update menu order");
                  console.error("Reorder error:", error);
                }
              }}
              fetchMenu={fetchMenu}
              selectedTypeId={selectedTypeId}
              typeData={typeData}
              menuData={menuData}
              fetchType={fetchType}
            />
          </Col>
        </Row>
      </Container>

      {/* Menu Type Form Modal */}
      <Modal isOpen={isTypeOpen} toggle={toggleType} centered>
        <ModalHeader toggle={toggleType}>
          <i className="ri-folder-add-line me-2" />
          {editingType ? "Edit Menu Type" : "Add Menu Type"}
        </ModalHeader>

        <ModalBody>
          <form onSubmit={formik.handleSubmit}>
            <div className="mb-3">
              <Label htmlFor="typeName">
                Name <span className="text-danger">*</span>
              </Label>
              <Input
                id="typeName"
                name="name"
                type="text"
                value={formik.values.name}
                onChange={formik.handleChange}
                placeholder="Enter menu type name (e.g., Header, Footer)"
                required
              />
            </div>
            <div className="d-flex gap-2 justify-content-end">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={toggleType}
              >
                <i className="ri-close-line me-1" />
                Cancel
              </button>
              <button type="submit" className="btn btn-success">
                <i className="ri-save-line me-1" />
                {editingType ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        show={isOpenDelete}
        onCloseClick={toggleDelete}
        onDeleteClick={handleDelete}
      />
    </div>
  );
};

export default Menu;
